import { css, cx } from "@emotion/css";
import { themeTokens, colorScales, space } from "@octopusdeploy/design-system-tokens";
import type { MouseEventHandler } from "react";
import type React from "react";
import { useRef } from "react";
import { useOnIsHoveredChanged } from "../../hooks";
import type { DesignSystemLinkHref, ShowLinkAsActive } from "../../routing/OctopusRoutingContext";
import { useIsUrlActive, useOctopusLinkComponent } from "../../routing/OctopusRoutingContext";
import { resetStyles } from "../../utils/resetStyles";
import { Tooltip } from "../Tooltip/Tooltip";
import { sideNavBarTabStyles } from "./sideNavBarTabStyles";
interface SharedSideNavBarLinkTabProps {
    /**
     * The icon for this link.
     */
    icon: React.ReactNode;
    /**
     * The link location.
     */
    href: DesignSystemLinkHref;
    showLinkAsActive?: ShowLinkAsActive;
    /**
     * The onClick handler associated with the tab.
     */
    onClick?: MouseEventHandler<Element>;
    /**
     * The hover handler associated with the tab.
     */
    onIsHoveredChanged?: (isHovered: boolean) => void;
}
interface SideNavBarLinkTabIconOnlyProps extends SharedSideNavBarLinkTabProps {
    /**
     * The required accessible name for this icon link.
     */
    accessibleName: string;
}
interface SideNavBarLinkTabWithLabelProps extends SharedSideNavBarLinkTabProps {
    /**
     * An optional accessible name for this icon link. If no accessible name is provided the label will be used.
     */
    accessibleName?: string;
    /**
     * The label for this icon link.
     */
    label: string;
}
type SideNavBarLinkTabIconLinkProps = SideNavBarLinkTabIconOnlyProps | SideNavBarLinkTabWithLabelProps;
export function SideNavBarLinkTab({ icon, showLinkAsActive, href, accessibleName, onClick, onIsHoveredChanged, ...props }: SideNavBarLinkTabIconLinkProps) {
    const Link = useOctopusLinkComponent();
    const linkRef = useRef<HTMLAnchorElement | null>(null);
    const isUrlActive = useIsUrlActive();
    const isActive = isUrlActive(href, showLinkAsActive ?? "if path partially matches");
    useOnIsHoveredChanged(linkRef, onIsHoveredChanged);
    const label = "label" in props ? props.label : undefined;
    const tab = (<Link ref={linkRef} className={sideNavBarLinkTabStyles} aria-current={isActive ? "page" : undefined} href={href} aria-label={accessibleName} onClick={onClick} disableImplicitEventTracking={true}>
            {icon}
            {label}
        </Link>);
    // There's no label. Show a tooltip based on the accessibility label if possible.
    if (!label && accessibleName) {
        return (<Tooltip content={accessibleName} className={tooltipStyles}>
                {tab}
            </Tooltip>);
    }
    return tab;
}
const sideNavBarLinkTabStyles = cx(css(resetStyles.anchor, {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: space[2],
    "&[aria-current=page]": {
        color: themeTokens.color.text.selected,
        ":hover": {
            background: themeTokens.color.background.secondary.hover,
        },
        "&::after": {
            content: "\"\"",
            position: "absolute",
            right: "0",
            height: "100%",
            borderTopLeftRadius: "4px",
            borderBottomLeftRadius: "4px",
            width: "4px",
            background: colorScales.blue["400"],
        },
    },
}), sideNavBarTabStyles);
const tooltipStyles = css({
    width: "100%",
});
